import { yaNative } from '@weberus/ui/utils/ya-native';

import type { AppVersionCallbackParam } from '@yanolja-rnd/ya-native/interfaces';
/**
 * 앱 버전이 지원되는지 확인하는 비동기 함수.
 *
 * `yaNative`의 API를 통해 현재 앱 버전을 가져와 대상 버전과 비교하여 지원 여부를 반환한다.
 *
 * @param targetVersion 체크할 대상 버전 (지원 여부를 판단할 기준 버전)
 * @param targetPlatform (선택 사항) 체크할 플랫폼. 버전만 비교할 경우 생략 가능.
 * @returns 지원 여부 (Promise<boolean>)
 */
export const isSupportedAppVersion = async (
  targetVersion: string,
  targetPlatform?: AppVersionCallbackParam['platform'],
): Promise<boolean> => {
  if (!yaNative.isEnabled) {
    return false;
  }
  const versionInfo = await new Promise<AppVersionCallbackParam | null>((resolve) => {
    yaNative.isEnabled &&
      yaNative.appVersion((success, info) => {
        resolve(success ? info : null);
      });
  });
  if (!versionInfo) {
    return false;
  }
  return checkVersion(versionInfo.version, targetVersion, versionInfo.platform, targetPlatform);
};

/**
 * 두 앱 버전을 비교하여 대상 버전 이상인지 확인하는 함수.
 *
 * 주어진 앱 버전과 대상 버전을 비교하며, 플랫폼도 일치해야 하는 경우 이를 함께 확인한다.
 *
 * @param version 현재 앱 버전 (체크 대상 버전)
 * @param targetVersion 비교 기준이 되는 대상 버전
 * @param platform (선택 사항) 현재 앱 플랫폼
 * @param targetPlatform (선택 사항) 비교 기준이 되는 플랫폼 (일치 여부 확인)
 * @returns 대상 버전 이상 및 (선택적으로) 플랫폼 일치 여부 (boolean)
 */
export const checkVersion = (
  version: string,
  targetVersion: string,
  platform?: AppVersionCallbackParam['platform'],
  targetPlatform?: AppVersionCallbackParam['platform'],
): boolean => {
  if (
    !isValidVersionFormat(version) || // 버전 형식이 올바르지 않은 경우
    !isValidVersionFormat(targetVersion) ||
    (targetPlatform && platform !== targetPlatform) // 플랫폼이 일치하지 않는 경우
  ) {
    return false;
  }
  const [major, minor, patch] = version.split('.').map(Number);
  const [tMajor, tMinor, tPatch] = targetVersion.split('.').map(Number);
  return (
    major > tMajor || (major === tMajor && minor > tMinor) || (major === tMajor && minor === tMinor && patch >= tPatch)
  );
};

/**
 * 입력된 문자열이 시맨틱 버전 형식인지 확인하는 함수.
 *
 * 시맨틱 버전 형식: "X.Y.Z" (X, Y, Z는 모두 숫자)
 *
 * @param version 확인할 버전 문자열
 * @returns 시맨틱 버전 형식 여부 (boolean)
 */
const isValidVersionFormat = (version: string): boolean => {
  // eslint-disable-next-line prefer-named-capture-group
  const pattern = /^(\d+)\.(\d+)\.(\d+)$/;
  return pattern.test(version);
};
